import React, { FC } from "react";
import {
  Badge,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { PictureOutlined, UserOutlined } from "@ant-design/icons";
import { MachineOverviewItem } from "shared/api";

const { Text, Title } = Typography;

const MachineCard: FC<{ item: MachineOverviewItem }> = ({ item }) => {
  const {
    machine_name,
    machine_image,
    open_tasks,
    max_priority,
    tasks_by_user,
  } = item;

  const assigned = Object.entries(tasks_by_user ?? {});
  const priorityStr = String(max_priority ?? "").trim();

  return (
    <Card
      hoverable
      cover={
        <div className="flex h-40 w-full items-center justify-center overflow-hidden">
          {machine_image ? (
            <img
              alt={machine_name}
              className="h-full w-full object-contain"
              src={machine_image}
            />
          ) : (
            <PictureOutlined className="text-5xl text-neutral-300" />
          )}
        </div>
      }
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size="middle" className="w-full">
        <Title className="!m-0 truncate" level={5} title={machine_name}>
          {machine_name}
        </Title>

        <Row gutter={[12, 12]} align="top">
          <Col flex="auto">
            <Statistic
              title="Open Tasks"
              value={open_tasks}
              valueStyle={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}
            />
          </Col>
          {priorityStr && (
            <Col flex="none">
              <Space align="center" size={8}>
                <Text type="secondary" className="text-xs uppercase">
                  Max Priority
                </Text>
                <Tag
                  className="!m-0"
                  color={
                    [
                      "#237804",
                      "#237804",
                      "#237804",
                      "#d46b08",
                      "#d46b08",
                      "#cf1322",
                    ][Number(max_priority)] || "#cf1322"
                  }
                >
                  {priorityStr}
                </Tag>
              </Space>
            </Col>
          )}
        </Row>

        <>
          <Divider className="!my-0" />
          <div>
            <Text
              type="secondary"
              className="mb-2 block text-xs uppercase tracking-wide"
            >
              Assigned to
            </Text>
            {assigned.length > 0 ? (
              <ul className="m-0 flex max-h-28 list-none flex-col gap-1.5 overflow-y-auto p-0">
                {assigned.map(([name, count]) => (
                  <li
                    key={name}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <UserOutlined className="shrink-0 text-neutral-400" />
                      <Text className="truncate text-sm" title={name}>
                        {name}
                      </Text>
                    </span>
                    <Badge
                      className="shrink-0"
                      color="blue"
                      count={count}
                      showZero
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <Text type="secondary" className="text-sm">
                No assigned users
              </Text>
            )}
          </div>
        </>
      </Space>
    </Card>
  );
};

export { MachineCard };
